'use client'

/**
 * BookingContext
 *
 * Provides tenant and property context for public booking pages.
 * Context is resolved server-side and passed in, or resolved client-side as fallback.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react'
import { useLocale } from 'next-intl'
import type { BookingContext as ServerBookingContext } from '@/services/booking-context'
import { pickBookingLogo } from '@/lib/booking-logo'
import { buildBookingSubdomainUrl } from '@/lib/booking-domain'

export interface BookingContextType {
  // Tenant info
  tenantId: string | null
  tenantCode: string | null
  tenantName: string | null
  /**
   * Booking-site logo for the page's language (TKT-20261102-0007): the Arabic
   * logo on Arabic pages, the English logo otherwise, with fallbacks applied.
   * Follows the locale, so it swaps as soon as the visitor switches language.
   */
  tenantLogo: string | null
  tenantDescription: string | null

  // Property info (null if showing all properties)
  propertyId: string | null
  propertyName: string | null
  propertySlug: string | null

  // Footer / contact info
  propertyPhone: string | null
  propertyEmail: string | null
  propertyAddress: string | null
  propertyGoogleMapUrl: string | null
  tenantBrandName: string | null
  tenantPhone: string | null
  tenantEmail: string | null
  tenantSocialLinks: Record<string, string> | null
  tenantNameAr: string | null
  vatNumber: string | null
  crNumber: string | null
  pdfFooterNote: string | null
  paymentMethods: Array<{ code: string; name: string }> | null

  // Affiliate program
  affiliateProgramName: string | null
  allowAgencyOnboarding: boolean

  // CMS pages
  footerPages: Array<{ slug: string; title: string; title_ar: string | null }>
  headerPages: Array<{ slug: string; title: string; title_ar: string | null }>

  // About page
  hasAboutPage: boolean

  // Seasonal theme
  seasonalTheme: string | null

  // Mode flags
  showAllProperties: boolean
  isLoading: boolean
  error: string | null

  // Search state
  searchState: BookingSearchState
  setSearchState: (state: Partial<BookingSearchState>) => void

  // Cart state
  selectedRoomType: SelectedRoomType | null
  setSelectedRoomType: (roomType: SelectedRoomType | null) => void
}

export interface BookingSearchState {
  checkIn: string | null // ISO date string
  checkOut: string | null // ISO date string
  adults: number
  children: number
  selectedPropertyId: string | null
}

export interface SelectedRoomType {
  id: string
  name: string
  rate: number
  currency: string
  nights: number
  totalAmount: number
}

const defaultSearchState: BookingSearchState = {
  checkIn: null,
  checkOut: null,
  adults: 2,
  children: 0,
  selectedPropertyId: null,
}

const BookingContext = createContext<BookingContextType | undefined>(undefined)

interface BookingProviderProps {
  children: ReactNode
  // Initial values from middleware/server
  initialTenantCode?: string
  initialPropertySlug?: string
  initialCustomDomain?: string
  // Server-resolved context (SSR)
  serverContext?: ServerBookingContext | null
  serverError?: string | null
}

export function BookingProvider({
  children,
  initialTenantCode,
  initialPropertySlug,
  initialCustomDomain,
  serverContext,
  serverError,
}: BookingProviderProps) {
  // Initialize state from server context if available
  const [tenantId, setTenantId] = useState<string | null>(
    serverContext?.tenantId || null
  )
  const [tenantCode, setTenantCode] = useState<string | null>(
    serverContext?.tenantCode || initialTenantCode || null
  )
  const [tenantName, setTenantName] = useState<string | null>(
    serverContext?.tenantName || null
  )
  const [tenantLogoAr, setTenantLogoAr] = useState<string | null>(
    serverContext?.tenantLogoAr || null
  )
  const [tenantLogoEn, setTenantLogoEn] = useState<string | null>(
    serverContext?.tenantLogoEn || null
  )
  const locale = useLocale()
  const tenantLogo = pickBookingLogo({ tenantLogoAr, tenantLogoEn }, locale)
  const [tenantDescription, setTenantDescription] = useState<string | null>(
    serverContext?.tenantDescription || null
  )

  const [propertyId, setPropertyId] = useState<string | null>(
    serverContext?.propertyId || null
  )
  const [propertyName, setPropertyName] = useState<string | null>(
    serverContext?.propertyName || null
  )
  const [propertySlug, setPropertySlug] = useState<string | null>(
    serverContext?.propertySlug || initialPropertySlug || null
  )

  const [showAllProperties, setShowAllProperties] = useState<boolean>(
    serverContext?.showAllProperties ?? !initialPropertySlug
  )

  // Footer fields
  const [propertyPhone, setPropertyPhone] = useState<string | null>(serverContext?.propertyPhone || null)
  const [propertyEmail, setPropertyEmail] = useState<string | null>(serverContext?.propertyEmail || null)
  const [propertyAddress, setPropertyAddress] = useState<string | null>(serverContext?.propertyAddress || null)
  const [propertyGoogleMapUrl, setPropertyGoogleMapUrl] = useState<string | null>(serverContext?.propertyGoogleMapUrl || null)
  const [tenantBrandName, setTenantBrandName] = useState<string | null>(serverContext?.tenantBrandName || null)
  const [tenantPhone, setTenantPhone] = useState<string | null>(serverContext?.tenantPhone || null)
  const [tenantEmail, setTenantEmail] = useState<string | null>(serverContext?.tenantEmail || null)
  const [tenantSocialLinks, setTenantSocialLinks] = useState<Record<string, string> | null>(serverContext?.tenantSocialLinks || null)
  const [tenantNameAr, setTenantNameAr] = useState<string | null>(serverContext?.tenantNameAr || null)
  const [vatNumber, setVatNumber] = useState<string | null>(serverContext?.vatNumber || null)
  const [crNumber, setCrNumber] = useState<string | null>(serverContext?.crNumber || null)
  const [pdfFooterNote, setPdfFooterNote] = useState<string | null>(serverContext?.pdfFooterNote || null)
  const [paymentMethods, setPaymentMethods] = useState<Array<{ code: string; name: string }> | null>(serverContext?.paymentMethods || null)
  const [affiliateProgramName, setAffiliateProgramName] = useState<string | null>(serverContext?.affiliateProgramName || null)
  const [allowAgencyOnboarding, setAllowAgencyOnboarding] = useState<boolean>(serverContext?.allowAgencyOnboarding ?? false)
  const [footerPages, setFooterPages] = useState<Array<{ slug: string; title: string; title_ar: string | null }>>(serverContext?.footerPages || [])
  const [headerPages, setHeaderPages] = useState<Array<{ slug: string; title: string; title_ar: string | null }>>(serverContext?.headerPages || [])
  const [hasAboutPage, setHasAboutPage] = useState<boolean>(serverContext?.hasAboutPage ?? false)
  const [seasonalTheme, setSeasonalTheme] = useState<string | null>(serverContext?.seasonalTheme || null)

  // If server provided context, we're not loading
  const [isLoading, setIsLoading] = useState<boolean>(!serverContext && !serverError)
  const [error, setError] = useState<string | null>(serverError || null)

  const [searchState, setSearchStateInternal] =
    useState<BookingSearchState>(() => ({
      ...defaultSearchState,
      selectedPropertyId: serverContext?.propertyId || null,
    }))
  const [selectedRoomType, setSelectedRoomType] =
    useState<SelectedRoomType | null>(null)

  const setSearchState = useCallback((state: Partial<BookingSearchState>) => {
    setSearchStateInternal((prev) => ({ ...prev, ...state }))
  }, [])

  // Only fetch client-side if server didn't provide context
  useEffect(() => {
    // Skip if server already resolved context
    if (serverContext || serverError) {
      return
    }

    async function resolveContext() {
      try {
        setIsLoading(true)
        setError(null)

        // Build hostname for resolution
        let hostname: string

        if (initialCustomDomain) {
          hostname = initialCustomDomain
        } else if (initialTenantCode) {
          hostname = new URL(
            buildBookingSubdomainUrl(initialTenantCode, initialPropertySlug)
          ).hostname
        } else {
          setError('No booking context provided')
          setIsLoading(false)
          return
        }

        const response = await fetch(
          `/api/internal/resolve-domain?hostname=${encodeURIComponent(hostname)}`
        )
        const data = await response.json()

        if (!response.ok) {
          setError(data.error || 'Failed to resolve booking context')
          setIsLoading(false)
          return
        }

        const { context } = data

        setTenantId(context.tenantId)
        setTenantCode(context.tenantCode)
        setTenantName(context.tenantName)
        setTenantLogoAr(context.tenantLogoAr ?? null)
        setTenantLogoEn(context.tenantLogoEn ?? null)
        setTenantDescription(context.tenantDescription)
        setPropertyId(context.propertyId)
        setPropertyName(context.propertyName)
        setPropertySlug(context.propertySlug)
        setShowAllProperties(context.showAllProperties)
        setPropertyPhone(context.propertyPhone)
        setPropertyEmail(context.propertyEmail)
        setPropertyAddress(context.propertyAddress)
        setPropertyGoogleMapUrl(context.propertyGoogleMapUrl)
        setTenantBrandName(context.tenantBrandName)
        setTenantPhone(context.tenantPhone)
        setTenantEmail(context.tenantEmail)
        setTenantSocialLinks(context.tenantSocialLinks)
        setTenantNameAr(context.tenantNameAr)
        setVatNumber(context.vatNumber)
        setCrNumber(context.crNumber)
        setPdfFooterNote(context.pdfFooterNote)
        setPaymentMethods(context.paymentMethods)
        setAffiliateProgramName(context.affiliateProgramName)
        setAllowAgencyOnboarding(context.allowAgencyOnboarding)
        setFooterPages(context.footerPages || [])
        setHeaderPages(context.headerPages || [])
        setHasAboutPage(context.hasAboutPage ?? false)
        setSeasonalTheme(context.seasonalTheme || null)

        if (context.propertyId) {
          setSearchStateInternal((prev) => ({
            ...prev,
            selectedPropertyId: context.propertyId,
          }))
        }

        setIsLoading(false)
      } catch (err) {
        console.error('Failed to resolve booking context:', err)
        setError('Failed to load booking site')
        setIsLoading(false)
      }
    }

    resolveContext()
  }, [initialTenantCode, initialPropertySlug, initialCustomDomain, serverContext, serverError])

  const value: BookingContextType = {
    tenantId,
    tenantCode,
    tenantName,
    tenantLogo,
    tenantDescription,
    propertyId,
    propertyName,
    propertySlug,
    propertyPhone,
    propertyEmail,
    propertyAddress,
    propertyGoogleMapUrl,
    tenantBrandName,
    tenantPhone,
    tenantEmail,
    tenantSocialLinks,
    tenantNameAr,
    vatNumber,
    crNumber,
    pdfFooterNote,
    paymentMethods,
    affiliateProgramName,
    allowAgencyOnboarding,
    footerPages,
    headerPages,
    hasAboutPage,
    seasonalTheme,
    showAllProperties,
    isLoading,
    error,
    searchState,
    setSearchState,
    selectedRoomType,
    setSelectedRoomType,
  }

  return (
    <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
  )
}

export function useBooking(): BookingContextType {
  const context = useContext(BookingContext)
  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider')
  }
  return context
}

/**
 * Hook to get booking context with loading/error states handled
 */
export function useBookingContext(): BookingContextType & { ready: boolean } {
  const context = useBooking()
  return {
    ...context,
    ready: !context.isLoading && !context.error && !!context.tenantId,
  }
}
